import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { I18NService } from '@ebw-monorepo/i18n-translate';
import { EbwModalService } from '@ebw-monorepo/modal';
import { FlatNode } from 'libs/ng-components/map/src/lib/models/ebw-tree.model';
import { CoordsStringPipe } from 'libs/ng-components/map/src/lib/pipe/coords.pipe';
import { FeatureSelectService } from 'libs/ng-components/map/src/lib/services/feature-select.service';
import { TelcoService } from 'libs/ng-components/map/src/lib/services/telco.service';
import { Subject, Subscription, debounceTime, distinctUntilChanged, filter, switchMap, takeUntil } from 'rxjs';
import { OtdrGeometryStyleEnum } from '../../enum/otdr-geometry-style.enum';
import { OtdrStatusEnum } from '../../enum/otdr-status.enum';
import { OTDRButton, OtdrParsedResponse } from '../../model/otdr.model';

@Component({
  selector: 'ebw-monorepo-default-otdr-form',
  templateUrl: './default-otdr-form.component.html',
  styleUrls: ['./default-otdr-form.component.scss'],
  providers: [CoordsStringPipe]
})
export class DefaultOtdrFormComponent implements OnInit, OnDestroy {
  /**
   * Risultati trovati nell'ultima ricerca effettuata sul campo 'device_urn'
   */
  searchResultsDeviceUrn: any[] = [];
  /**
   * bottoni footer
   */
  buttons: OTDRButton[] = [
    { label: 'Avvia', type: 'flat' },
    { label: 'Svuota', type: 'outline' }
  ];
  form: FormGroup;
  private destroy$ = new Subject<void>();

  rootUrn = '';
  objectValue = null;

  otdrOutcome: OtdrParsedResponse;

  autocompleteSubscription: Subscription;

  // ------------------------------------------------------------------------------------------------

  constructor(
    private _fb: FormBuilder,
    private _fsService: FeatureSelectService,
    private _ebwModalService: EbwModalService,
    private _i18NService: I18NService,
    private _telcoService: TelcoService,
    private _coordsPipe: CoordsStringPipe
  ) {}

  // ------------------------------------------------------------------------------------------------

  ngOnInit(): void {
    this.initForm();
    // quando viene chiamata apertura pannello OTDR controllo se ci sono parametri in ingresso
    // se è così, l'apertura dell'OTDR è stata triggerata dal dettaglio di un asset
    // se non c'è niente è stato aperto dal menu
    this._fsService.openOtdrPanel$
      .pipe(takeUntil(this.destroy$), filter(Boolean))
      .subscribe(() => this.takeRootParams());
  }

  // ------------------------------------------------------------------------------------------------

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this._telcoService.clearOtdrTemporaryLayer();
  }

  // ------------------------------------------------------------------------------------------------

  /**
   * Gestisce l'output dei click sui bottoni del footer
   * @param value
   */
  onChangeComponentHandler(value: string) {
    // pulisce eventuali geometrie renderizzate precedentemente
    this._telcoService.clearOtdrTemporaryLayer();
    switch (value) {
      case 'Avvia':
        this.submitForm();
        break;
      default:
        // resetta il form
        this.resetForm();
        break;
    }
  }

  // ------------------------------------------------------------------------------------------------

  async handleTree(searchResult: { desc: string; urns: string; type: string }) {
    this.rootUrn = searchResult.urns;
  }

  // ------------------------------------------------------------------------------------------------

  async handleTreeItemSelection(node: { name: string; urn: string }) {
    const { name, urn } = node;
    this.objectValue = { name, urn };
    // se non è abilitato non si possono modificare i valori
    this.form.get('object').enable();
    this.form.patchValue({ object: name });
    this.form.get('object').disable();
  }

  // ------------------------------------------------------------------------------------------------

  private takeRootParams() {
    const { urn, name } = history.state;
    if (urn && name) {
      this.initForm();
      this.form.patchValue({ device_urn: name });
    }
  }

  // ------------------------------------------------------------------------------------------------

  private initForm(): void {
    if (this.form) {
      this.resetForm();
    } else {
      this.form = this._fb.group({
        device_urn: ['', [this.checkIfOptionIsSelected.bind(this)]],
        object: [{ value: '', disabled: true }, [Validators.required]],
        distance: [0, [Validators.required]]
      });
    }
    this.setupAutocomplete();
  }

  // ------------------------------------------------------------------------------------------------

  private resetForm(): void {
    // svuota le opzioni della possibile ricerca precedente
    this.searchResultsDeviceUrn = [];
    // cancello l'albero se c'è
    this.rootUrn = '';
    // il valore selezionato dall'albero
    this.objectValue = null;
    // la risposta di una precedente analisi OTDR
    this.otdrOutcome = null;
    // resetto i campi del form
    this.form.reset({ device_urn: '', object: '', distance: 0 });
  }

  // ------------------------------------------------------------------------------------------------

  private async submitForm(): Promise<void> {
    if (this.form.invalid) {
      return;
    }
    const { distance, device_urn } = this.form.value;
    // faccio questo controllo perchè l'utente deve aver selezionato un opzione valida in ogni campo del form
    if (!device_urn || !distance || !this.objectValue) {
      const content = this._i18NService.getTranslateParameter('map.sidebar.otdr.default.invalid_error_option');
      const title = this._i18NService.getTranslateParameter('common.warning');
      this._ebwModalService.openWarningModal({ title, content });
      return;
    }
    const otdrResponse = await this._telcoService.startOtdrDefault({
      device_urn,
      distance,
      object: this.objectValue.urn
    });

    const { geometry, properties } = otdrResponse;

    // pulisco i layer precdentemente creati per rendere il processo consistente
    this._telcoService.clearOtdrTemporaryLayer();
    // se non esiste lo stato esco
    if (!properties?.status) {
      const content = this._i18NService.getTranslateParameter('common.data_retrieving_error');
      const title = this._i18NService.getTranslateParameter('common.error');
      this._ebwModalService.openWarningModal({ title, content });
      return;
    }
    // se non esiste la geometria mostro messaggio di errore
    if (!geometry) {
      const content = this._i18NService.getTranslateParameter('map.sidebar.otdr.default.no_geometry_error');
      const title = this._i18NService.getTranslateParameter('common.warning');
      this._ebwModalService.openWarningModal({ title, content });
      return;
    }
    // ---------------------------------------------------------
    // l'end point rappresenta l'ultimo punto della geometria,
    // se quest'ultima è lunga almento quanto la distanza,
    // allora esso coincide con il punto di guasto
    // ---------------------------------------------------------
    let geometryStyle = '';
    let endPointStyle = '';
    let otdrOutcome = '';
    if (properties.status === OtdrStatusEnum.ok) {
      endPointStyle = OtdrGeometryStyleEnum.brokenDownPoint;
      geometryStyle = OtdrGeometryStyleEnum.successLinePath;
      otdrOutcome = this._i18NService.getTranslateParameter('map.sidebar.otdr.default.response.success');
    }
    if (properties.status === OtdrStatusEnum.ko) {
      endPointStyle = OtdrGeometryStyleEnum.lastPoint;
      geometryStyle = OtdrGeometryStyleEnum.failureLinePath;
      otdrOutcome = this._i18NService.getTranslateParameter('map.sidebar.otdr.default.response.failure');
    }

    this.otdrOutcome = {
      geometry,
      endPoints: properties.breakpoints,
      description: otdrOutcome
    };

    this.otdrOutcome.endPoints.forEach((point) => {
      this._telcoService.centerMapOnGeometry(point, endPointStyle, true);
    });
    this._telcoService.centerMapOnGeometry(geometry, geometryStyle);
  }

  // ------------------------------------------------------------------------------------------------

  private setupAutocomplete(): void {
    if (this.autocompleteSubscription) {
      return;
    }
    // mi sottoscrivo ai cambiamenti dell'autocomplete per avviare la ricerca su solr
    this.autocompleteSubscription = this.form
      .get('device_urn')
      .valueChanges.pipe(
        // tempo minimo tra una richiesta e l'altra
        debounceTime(300),
        // cerco solo con almeno 3 caratteri
        filter((value: string) => value?.length > 2),
        // solo se il testo cambia
        distinctUntilChanged(),
        // ricerca solr
        switchMap((value: string) => this._telcoService.otdrSearch(value))
      )
      .subscribe({
        next: (results: any[]) => {
          // Se non ho trovato risultati
          if (!results.length) {
            this.searchResultsDeviceUrn = [];
            return;
          }
          // altrimenti popolo le opzioni dell'autocomplete
          this.searchResultsDeviceUrn = results;
        },
        error: (error) => {
          // setto eventuali errori e pulisco l'array
          console.error('[DefaultOtdrFormComponent][setupAutocomplete]', error);
          this.searchResultsDeviceUrn = [];
          this.form.get('device_urn').setErrors({ solrError: true });
          // mi risottoscrivo altrimenti le ricerche successive non funzionano
          this.autocompleteSubscription.unsubscribe();
          this.setupAutocomplete();
        }
      });
  }

  // ------------------------------------------------------------------------------------------------

  private checkIfOptionIsSelected(control: any) {
    if (!this.searchResultsDeviceUrn.length) {
      return null;
    }
    try {
      // controllo se il nome presente nel form corrisponde ad una delle opzioni ricercate
      const deviceUrnInvalid = !this.searchResultsDeviceUrn.find((obj) => obj.desc === control.value);
      // se non lo è metto un errore sul campo input
      return deviceUrnInvalid ? { optionError: true } : null;
    } catch (error) {
      console.error('[DefaultOtdrFormComponent][checkIfOptionIsSelected]', error);
      return null;
    }
  }

  // ------------------------------------------------------------------------------------------------

  copy = (coords: number[]) => this._telcoService.copyToClipboard(this._coordsPipe.transform(coords));

  // ------------------------------------------------------------------------------------------------

  goToDetail(node: FlatNode) {
    console.warn('[DefaultOtdrFormComponent][goToDetail] Non ancora disponibile');
  }
}
